/**
 * Single source of truth for classifying the cached client role string.
 *
 * The role saved to `localStorage("userRole")` at login is LOWER-CASED but
 * KEEPS spaces: the central role "Tenant Owner" is stored as "tenant owner",
 * NOT "tenant_owner" (see Login.tsx). Components that matched the cached role
 * against an underscore list (`["admin","tenant_owner",...].includes(role)`)
 * therefore failed to recognize tenant owners — and custom staff roles like
 * "coach"/"manager" — as privileged, and hid placement-only USAG team slots
 * from them in the enrollment picker. That is the "owner/coach can't enroll
 * the athlete into the 12-hour team" bug, reported ~4× because each previous
 * fix patched a sibling component that happened to use the space form while
 * the slot-filtering child kept the underscore form.
 *
 * Normalize once, here, and have every call site use these helpers instead of
 * re-deriving role classification inline. The `available_for_registration`
 * self-service gate is bypassed for anyone these classify as admin/staff.
 * See memory `project_available_for_registration_staff_bypass`.
 */

import { safeGetItem } from "@mediatechgroup/app-version";

/** Lower-case, trim, and collapse spaces to underscores ("Tenant Owner" -> "tenant_owner"). */
export const normalizeRole = (role?: string | null): string =>
  (role ?? "").toLowerCase().trim().replace(/\s+/g, "_");

/** Admin-equivalent roles, in normalized (underscore) form. */
export const ADMIN_ROLES: ReadonlySet<string> = new Set([
  "admin",
  "super_admin",
  "tenant_owner",
  "platform_admin",
  "owner",
]);

/** Roles that are explicitly NOT staff. Everything else concrete is staff. */
const NON_STAFF_ROLES: ReadonlySet<string> = new Set([
  "member",
  "customer",
  "kiosk",
]);

export const isAdminRole = (role?: string | null): boolean =>
  ADMIN_ROLES.has(normalizeRole(role));

/**
 * Staff = any concrete role that is neither admin-family nor a non-staff role.
 * This matches the Redux `setIsStaff` semantics so custom roles ("manager",
 * "senior_coach", ...) are recognized without an allow-list to maintain.
 */
export const isStaffRole = (role?: string | null): boolean => {
  const r = normalizeRole(role);
  return r.length > 0 && !ADMIN_ROLES.has(r) && !NON_STAFF_ROLES.has(r);
};

/** True for staff/admin/owner — the surfaces that bypass the self-service gate. */
export const isAdminOrStaffRole = (role?: string | null): boolean =>
  isAdminRole(role) || isStaffRole(role);

/**
 * True for the member-PORTAL account roles: member and customer.
 *
 * Customers are member-shaped accounts (a membership with athletes and
 * registrations) minted by guest camp/clinic/event registration. Since
 * 2026-08-28 they can sign in (set-password invites) and enrolling an
 * athlete auto-promotes them to member — so the portal treats them as
 * members from the start. Excluding them produced Access Denied on every
 * portal page for a signed-in customer (Solomon Fair follow-up).
 * Authorization stays server-side and per-membership either way.
 */
export const isMemberRole = (role?: string | null): boolean => {
  const r = normalizeRole(role);
  return r === "member" || r === "customer";
};

/** True for the customer account role specifically. */
export const isCustomerRole = (role?: string | null): boolean =>
  normalizeRole(role) === "customer";

/** True for the platform Super Admin role (central "Super Admin"). */
export const isSuperAdminRole = (role?: string | null): boolean =>
  normalizeRole(role) === "super_admin";

/** Reads the cached login role from localStorage and classifies it. */
export const storedRoleIsAdminOrStaff = (): boolean =>
  isAdminOrStaffRole(
    safeGetItem("userRole")
  );

/**
 * Pull the current role's permissions out of the /roles-with-permissions map.
 *
 * The backend keys that map by the EXACT stored `roles.name`, but the role in
 * localStorage was lower-cased at login (see the header note above). An exact
 * `map[userRole]` lookup therefore misses for any role that isn't already
 * all-lowercase and yields [] — which is not "fewer permissions" but ZERO, so
 * every permission-gated page renders Access Denied and the app looks wholly
 * broken.
 *
 * Two live routes into that state: tenants provisioned from the roles/permissions
 * supabase migration get TitleCase roles (Admin/Manager/Staff — the state
 * dev-tenant is in), and admins can name a new role anything they like in Roles
 * & Permissions ("Front Desk"). Match case-insensitively so neither locks
 * anyone out. Compared with normalizeRole so "Tenant Owner"/"tenant owner"
 * agree on spacing too.
 */
export const selectPermissionsForRole = <T>(
  permissionsByRole: Record<string, T[]> | null | undefined,
  role?: string | null
): T[] => {
  const map = permissionsByRole || {};
  const wanted = normalizeRole(role);
  if (!wanted) return [];
  const key = Object.keys(map).find((name) => normalizeRole(name) === wanted);
  return key ? map[key] || [] : [];
};
